import { useApolloClient, useLazyQuery, useMutation } from "@apollo/client";
import * as Crypto from "expo-crypto";
import { Base64 } from "js-base64";
import type * as ld from "launchdarkly-js-client-sdk";
import type React from "react";
import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { Platform } from "react-native";
import { AuthenticateMethod, type AuthenticateResult, gql, type ProfileQuery, type UserSegmentationData } from "@medbillai/generated-graphql-types";
import { isWeb, type OAuthProviderType } from "@medbillai/ui";
import { mapStytchTokenTypeToAuthenticateMethod, type StytchTokenType } from "../../provider/stytch";
import { useStartOAuth, useStytch } from "../../provider/stytch/useStytch";
import { analyticsService } from "../analytics";
import { errorHandling } from "../apollo/utils";
import { useLaunchDarklyIdentify } from "../flag";
import { AUTHENTICATED_PENDING_IDENTITY, useDeclareFlagIdentity } from "../flag/shared";
import { pushNotificationsService } from "../push-notifications/service";
import { sentryService } from "../sentry";
import { useSignUpOnboardingStore } from "../sign-up-onboarding/signUpOnboardingStore";
import { SingularEvents, singularService } from "../singular";
import { anonymousSessionStorage, userSegmentationStorage } from "../storage/instances";
import { AuthContext, type AuthContextInterface, type AuthenticatedPartner } from "./authContext";
import { initialAuthState, type User, usePersistedAuthReducer } from "./authState";
export interface AuthProviderProps {
  children?: React.ReactNode;
  authRedirectUri: (method: AuthenticateMethod) => string;
}
const normalizeError = (fallbackMessage: string) => (error: unknown): Error => {
  if (error instanceof Error) {
    return error;
  }
  // Special handling for GQL errors which can be an arbitrary object.
  if (error && typeof error === "object" && "message" in error) {
    return new Error(error.message as string);
  }
  if (typeof error === "string") {
    return new Error(error);
  }
  return new Error(fallbackMessage);
};
const profileError = normalizeError("Profile load failed");
const magicLinkError = normalizeError("Registration failed");
const oauthError = normalizeError("OAuth registration failed");
const logoutError = normalizeError("Logout failed");
const shadowLoginError = normalizeError("Shadow login failed");

/**
 * Generates PKCE code verifier and code challenge for OAuth authentication.
 * @returns Promise containing codeVerifier and codeChallenge strings
 */
const generatePKCEChallenge = async (): Promise<{
  codeVerifier: string;
  codeChallenge: string;
}> => {
  // Need to generate a new code verifier and challenge.
  // TODO: This is kind of a mess - is there a better way? RN doesn't have great support
  // for Buffer out of the box, and the polyfills don't implement base64url. Have had to
  // use js-base64, which is not ideal.
  const codeVerifierBytes = Crypto.getRandomBytes(44);
  const codeVerifier = Base64.fromUint8Array(codeVerifierBytes, true);
  const codeChallenge = Base64.fromUint8Array(
  // TODO: This is even worse...
  new Uint8Array(await Crypto.digest(Crypto.CryptoDigestAlgorithm.SHA256, Uint8Array.from(codeVerifier.split("").map(letter => letter.charCodeAt(0))))), true);
  return {
    codeVerifier,
    codeChallenge
  };
};
const sendMagicLinkMutation = gql(/* GraphQL */`
  mutation SendMagicLink($input: SendMagicLinkInput!) {
    sendMagicLink(input: $input) {
      ... on SendMagicLinkResponse {
        user {
          id
          isAdmin
          isShadow
          adminUserId
        }
        created
      }
      ... on FormError {
        message
        fields {
          path
          message
        }
      }
    }
  }
`);
const resendMagicLinkMutation = gql(/* GraphQL */`
  mutation ResendMagicLink($input: ResendMagicLinkInput!) {
    resendMagicLink(input: $input) {
      success
    }
  }
`);
const authenticateMutation = gql(/* GraphQL */`
  mutation Authenticate($input: AuthenticateInput!) {
    authenticate(input: $input) {
      ... on AuthenticateSuccess {
        success
        firstAuthentication
        email
        user {
          id
          isAdmin
          isShadow
          adminUserId
        }
      }
      ... on AuthenticateError {
        success
        errorType
      }
    }
  }
`);

// Like `authenticate`, but also redeems the invite and returns the bound partner.
const authenticateWithInviteMutation = gql(/* GraphQL */`
  mutation AuthenticateWithInvite(
    $input: AuthenticateInput!
    $inviteToken: String!
  ) {
    authenticateWithInvite(input: $input, inviteToken: $inviteToken) {
      ... on AuthenticateWithInviteSuccess {
        success
        firstAuthentication
        email
        partner {
          id
          slug
          name
        }
        user {
          id
          isAdmin
          isShadow
          adminUserId
        }
      }
      ... on AuthenticateError {
        success
        errorType
      }
    }
  }
`);
const logoutMutation = gql(/* GraphQL */`
  mutation Logout {
    logout
  }
`);
const shadowLoginMutation = gql(/* GraphQL */`
  mutation ShadowLogin($input: ShadowLoginInput!) {
    shadowLogin(input: $input) {
      ... on AuthUser {
        id
        isAdmin
        isShadow
        adminUserId
      }
      ... on FormError {
        fields {
          path
          message
        }
      }
    }
  }
`);
const profileQuery = gql(/* GraphQL */`
  query Profile {
    me {
      id
      isAdmin
      primaryEmail
      createdAt
      lastAuthenticatedAt
      profile {
        name
      }
      segmentationData {
        features
      }
    }
  }
`);
export const AuthProvider = (props: AuthProviderProps) => {
  // Mutation functions for auth.
  const stytch = useStytch();
  const {
    authRedirectUri
  } = props;
  const [doSendMagicLink] = useMutation(sendMagicLinkMutation, {
    context: errorHandling("caller"),
    ignoreResults: true
  });
  const [doResendMagicLink] = useMutation(resendMagicLinkMutation, {
    context: errorHandling("caller"),
    ignoreResults: true
  });
  const [doAuthenticate] = useMutation(authenticateMutation, {
    context: errorHandling("caller"),
    ignoreResults: true
  });
  const [doAuthenticateWithInvite] = useMutation(authenticateWithInviteMutation, {
    context: errorHandling("caller"),
    ignoreResults: true
  });
  const [doLogout] = useMutation(logoutMutation, {
    context: errorHandling("no-log"),
    ignoreResults: true
  });
  const resetSignUpOnboardingStore = useSignUpOnboardingStore(state => state.reset);
  const [doShadowLogin, {
    called: shadowLoginCalled,
    reset: shadowLoginReset
  }] = useMutation(shadowLoginMutation);
  const apolloClient = useApolloClient();

  // Primary stateful store for the authentication state. This is persisted to localStorage so that
  // we can assume logged-in status across page loads.
  const {
    state,
    dispatch,
    initialize: initializeReducer
  } = usePersistedAuthReducer(initialAuthState);
  const didInitializeReducer = useRef(false);

  // Slightly hacky workaround to support loading state from localStorage - we need to wait for
  // a render via useEffect before accessing localStorage, due to the way that NextJS operates
  useEffect(() => {
    if (didInitializeReducer.current) {
      return;
    }
    didInitializeReducer.current = true;
    void initializeReducer();
  }, [initializeReducer]);
  const revokeStytchSession = useCallback(async () => {
    try {
      const tokens = stytch.session.getTokens();
      if (tokens?.session_jwt) {
        await stytch.session.revoke();
      }
    } catch (error) {
      console.error("[Stytch Provider] Error revoking session", error);
      // Do nothing
    }
  }, [stytch]);
  const logout = useCallback(async () => {
    dispatch({
      type: "LOGOUT"
    });
    try {
      await pushNotificationsService.deregisterDevice();
      // Revoke the client-side Stytch session first; this clears the
      // provider's token before the server-side logout.
      await revokeStytchSession();
      try {
        // This is only to forcefully revoke the session if one is still active. We want to ignore
        // any errors and continue with the client logout.
        await doLogout({
          errorPolicy: "ignore"
        });
      } catch {
        // Do nothing
      }
      // Clear any objects in the Apollo cache, since they are associated with the logged-in user.
      await apolloClient.clearStore();
      analyticsService.resetUser();
      sentryService.clearUser();
      singularService.resetUser();
      dispatch({
        type: "CLEAR_INTENDED_PATH"
      });
    } catch (error) {
      dispatch({
        type: "ERROR",
        error: logoutError(error)
      });
      throw error;
    }
  }, [doLogout, apolloClient, dispatch, revokeStytchSession]);

  // This function is used on application startup, and after shadow login.
  const [queryProfile] = useLazyQuery(profileQuery, {
    // Need to avoid the cache for shadow login.
    fetchPolicy: "no-cache",
    nextFetchPolicy: "no-cache",
    context: errorHandling("no-log")
  });
  const fetchProfile = useCallback(async (): Promise<ProfileQuery["me"] | undefined> => {
    try {
      const profileResult = await queryProfile();
      // Check for expiration of the user's session - in that case, just return undefined.
      // The calling code will see this and log the user out.
      if (profileResult.error?.graphQLErrors?.some(gqlErr => gqlErr.extensions?.code === "UNAUTHENTICATED" || gqlErr.message.includes("session token"))) {
        return;
      }
      if (profileResult.error?.graphQLErrors?.[0]) {
        throw profileError(profileResult.error.graphQLErrors[0]);
      }
      if (profileResult.error?.networkError) {
        throw profileError(profileResult.error.networkError);
      }
      const profileData = profileResult.data?.me;
      if (!profileData) {
        throw profileError("Missing profile data");
      }
      return profileData;
    } catch (error) {
      throw profileError(error);
    }
  }, [queryProfile]);
  const refreshProfile = useCallback(async () => {
    try {
      const profile = await fetchProfile();
      if (profile) {
        dispatch({
          type: "PROFILE_LOADED",
          profile
        });
      }
    } catch {
      // keep the cached profile
    }
  }, [fetchProfile, dispatch]);

  // Identify the user to LaunchDarkly whenever it changes. Fields are mostly
  // arbitrary, but we try to match those set in the API service.
  // https://docs.launchdarkly.com/sdk/features/identify#react-native
  // https://launchdarkly.com/docs/sdk/features/anonymous#expand-javascript-code-sample
  const userContext: ld.LDContext = useMemo(() => {
    if (!state.user?.id || !state.user?.profile?.email) {
      return {
        kind: "user",
        ...(Platform.OS !== "web" && {
          key: ""
        }),
        anonymous: true
      };
    }
    return {
      kind: "user",
      key: state.user.id,
      name: state.user.profile.name ?? state.user.profile.email,
      email: state.user.profile.email,
      isAdmin: state.user.isAdmin ?? false,
      createdAt: state.user.createdAt,
      lastAuthenticatedAt: state.user.lastAuthenticatedAt
    };
  }, [state.user?.id, state.user?.isAdmin, state.user?.profile?.email, state.user?.profile?.name, state.user?.createdAt, state.user?.lastAuthenticatedAt]);
  const identify = useLaunchDarklyIdentify();
  const declareFlagIdentity = useDeclareFlagIdentity();
  // Who the app currently considers the user, independent of whether we can
  // identify yet. `LOGIN_SUCCEEDED` lands before the profile does, so this is
  // the only signal available during that window.
  const flagIdentityKey = state.isAuthenticated ? state.user?.id ?? AUTHENTICATED_PENDING_IDENTITY : null;
  useEffect(() => {
    if (!identify || !state.isHydrated) {
      return;
    }
    // Declared before the early return below: on login, logout and shadow
    // login the identity changes while LaunchDarkly is still serving the
    // previous context, and reads must stop until the new identify lands.
    declareFlagIdentity(flagIdentityKey);
    // Authenticated but profile incomplete: wait. Identifying with the
    // placeholder `{ anonymous: true }` context would latch flag reads (or
    // used to) and fire exposures on the anon key before the real user
    // identify lands — ENG-7578.
    if (state.isAuthenticated && (!state.user?.id || !state.user?.profile?.email)) {
      return;
    }
    void identify(userContext, {
      identityKey: flagIdentityKey,
      // Logged-out visitors are intentionally anonymous — authorize reads so
      // logged-out flag targeting still works. Logged-in calls omit this and
      // default to authorizing only for non-anonymous contexts.
      ...(state.isAuthenticated ? {} : {
        authorizeFlagReads: true
      })
    });
  }, [userContext, identify, declareFlagIdentity, flagIdentityKey, state.isHydrated, state.isAuthenticated, state.user?.id, state.user?.profile?.email]);

  // This function is responsible for loading the profile data at application startup. It
  // serves double-duty as a validation of the session token when the application first
  // loads. If we fail to retrieve the profile, we assume the token is invalid and log the
  // user out.
  // The function also causes the profile to be fetched automatically if a user logs in.
  useEffect(() => {
    if (!state.isHydrated || state.error) {
      return;
    }
    if (!state.isAuthenticated) {
      // If we just finished hydrating the state, and the user is not logged in, set the
      // ready flag to true.
      if (!state.isReady) {
        dispatch({
          type: "AUTH_READY"
        });
      }
      return;
    }
    if (state.isReady && state?.user?.profile) {
      // If we already have a profile after initial startup, do nothing. Note
      // that this is mostly a defensive guard to prevent a double-fetch after
      // isReady is set to true during the initial run-through of this effect.
      // This is NOT hit upon first load, even when storage is rehydrated with a
      // profile, because isReady is still false immediately after rehydration.
      return;
    }
    // Otherwise, try to fetch profile data.
    void (async () => {
      try {
        const profile = await fetchProfile();
        if (!profile) {
          // Expired session - log the user out.
          console.debug("Failed to retrieve user profile at startup due to an expired session. Logging out.");
          await logout();
          return;
        }
        dispatch({
          type: "PROFILE_LOADED",
          profile
        });

        // Mark auth ready as soon as profile is loaded; do not block on side effects.
        if (!state.isReady) {
          dispatch({
            type: "AUTH_READY"
          });
        }

        // Run side effects without blocking readiness
        if (state?.user?.id && profile.primaryEmail) {
          if (!state.user.isShadow) {
            // We only register the device for non-shadow users, as well as send
            // analytics so we do not polute the data.
            await pushNotificationsService.registerDevice(profile.primaryEmail, state.user.id);
            analyticsService.identifyUser(state?.user?.id, {
              email: profile.primaryEmail,
              name: profile.profile.name ?? undefined,
              userSegmentFeatures: profile.segmentationData?.features ?? undefined
            });
            singularService.setUser({
              id: state?.user.id,
              email: profile.primaryEmail
            });
          }
          sentryService.setUser({
            id: state.user.id,
            email: profile.primaryEmail,
            shadowUser: state.user.isShadow
          });
        }
      } catch (error) {
        // Don't bother logging network errors.
        if (!(error instanceof TypeError && error.message === "Network request failed")) {
          console.error(`Failed to load profile: ${String(error)}`);
        }
        // Also sets isReady to true.
        dispatch({
          type: "ERROR",
          error: profileError(error)
        });
      }
    })();
  }, [state.isHydrated, state.isReady, state.isAuthenticated, state.error, state.user?.id, state.user?.isShadow, state.user?.profile, fetchProfile, logout, dispatch]);
  const resendMagicLink = useCallback(async ({
    email
  }: {
    email: string;
  }) => {
    const {
      codeVerifier,
      codeChallenge
    } = await generatePKCEChallenge();
    dispatch({
      type: "CODE_VERIFIER",
      codeVerifier
    });
    const result = await doResendMagicLink({
      variables: {
        input: {
          email,
          clientRedirectUri: authRedirectUri(AuthenticateMethod.MAGIC_LINK),
          codeChallenge
        }
      }
    });
    const data = result.data?.resendMagicLink;
    if (!data) {
      throw magicLinkError("Missing data on response");
    }
    if (!data.success) {
      throw magicLinkError("Failed to resend magic link");
    }
    return data;
  }, [dispatch, doResendMagicLink, authRedirectUri]);

  // Wrap all functions in useCallback to prevent state churn and unnecessary re-renders
  const finishOnboarding = useCallback(() => {
    dispatch({
      type: "FINISHED_ONBOARDING",
      hasFinishedOnboarding: true
    });
  }, [dispatch]);
  const sendMagicLink = useCallback(async ({
    email,
    botControlToken,
    userSegmentationData,
    inviteToken
  }: {
    email: string;
    botControlToken?: string;
    userSegmentationData?: UserSegmentationData;
    inviteToken?: string;
  }) => {
    const {
      codeVerifier,
      codeChallenge
    } = await generatePKCEChallenge();
    dispatch({
      type: "CODE_VERIFIER",
      codeVerifier
    });
    dispatch({
      type: "PENDING_INVITE_TOKEN",
      pendingInviteToken: inviteToken ?? null
    });

    // Determine the platform
    const sourcePlatform = Platform.OS;

    // Read anonymous session token if one exists from the pre-auth flow
    const anonymousSessionId = (await anonymousSessionStorage.get("token")) ?? undefined;
    const clientRedirectUri = authRedirectUri(AuthenticateMethod.MAGIC_LINK);

    // Will throw any errors up to the caller.
    const result = await doSendMagicLink({
      variables: {
        input: {
          email,
          clientRedirectUri,
          codeChallenge,
          sourcePlatform,
          botControlToken,
          userSegmentationData,
          anonymousSessionId
        }
      }
    });
    const data = result.data?.sendMagicLink;
    if (!data) {
      throw magicLinkError("Missing data on response");
    }
    if (data.__typename === "FormError") {
      return data;
    }
    if (data.__typename === "SendMagicLinkResponse") {
      // Clear the anonymous session token so it isn't re-sent on future logins.
      await anonymousSessionStorage.remove("token");
      if (isWeb && (userSegmentationData?.features || userSegmentationData?.useCases || userSegmentationData?.usageScopes)) {
        finishOnboarding();
      }
      resetSignUpOnboardingStore();
      return data;
    }
    throw magicLinkError("Unexpected response type");
  }, [dispatch, doSendMagicLink, authRedirectUri, resetSignUpOnboardingStore, finishOnboarding]);

  /**
   * This function is used to get the authentication token for the given method
   * and token. OAuth is handled by Stytch on the frontend (because it requires a
   * matching PKCE code challenge that only the backend can generate), so we need
   * to exchange the provider token for a Stytch session token. Magic link is
   * handled by the backend, so we can just return the token.
   *
   * Unhandled errors are thrown up to the caller.
   */
  const getAuthenticationToken = useCallback(async (method: StytchTokenType, token: string): Promise<{
    sessionToken: string;
  }> => {
    // Magic links and native OAuth(Apple) are handled by the backend.
    if (method === "magic_links" || method === "oauth-native") {
      return {
        sessionToken: token
      };
    }

    // For OAuth, we need to exchange the provider token for a Stytch session
    // token to be re-authenticated on the backend with the proper claims.
    // This session will get refreshed on the backend but the expiration
    // does not get updated.
    const oauthResponse = await stytch.oauth.authenticate(token, {
      session_duration_minutes: 5
    });
    if (!oauthResponse.session_jwt) {
      throw oauthError("Missing session token");
    }
    return {
      sessionToken: oauthResponse.session_jwt
    };
  }, [stytch]);
  const authenticate = useCallback(async ({
    method,
    token,
    userSegmentationData,
    inviteToken
  }: {
    method: StytchTokenType;
    token: string;
    userSegmentationData?: UserSegmentationData;
    inviteToken?: string;
  }): Promise<AuthenticateResult & {
    partner?: AuthenticatedPartner;
  }> => {
    dispatch({
      type: "LOGIN_REQUESTED"
    });
    const mappedMethod = mapStytchTokenTypeToAuthenticateMethod(method);
    const authError = mappedMethod === AuthenticateMethod.OAUTH ? oauthError : magicLinkError;
    try {
      const sessionData = await getAuthenticationToken(method, token);

      // Read anonymous session token if one exists from the pre-auth flow.
      // Only relevant for OAuth; magic link conversion is handled via sendMagicLink.
      const anonymousSessionId = mappedMethod === AuthenticateMethod.OAUTH ? (await anonymousSessionStorage.get("token")) ?? undefined : undefined;
      const input = {
        method: mappedMethod,
        token: sessionData.sessionToken,
        codeVerifier: state.codeVerifier,
        sourcePlatform: Platform.OS,
        userSegmentationData,
        anonymousSessionId
      };

      // Normalize the invite-mutation success to the plain AuthenticateSuccess
      // shape (plus partner) so callers handle one union. An explicit param
      // wins over the stash.
      const effectiveInviteToken = inviteToken ?? state.pendingInviteToken;
      const authResult = effectiveInviteToken ? await doAuthenticateWithInvite({
        variables: {
          input,
          inviteToken: effectiveInviteToken
        }
      }).then(result => {
        const withInvite = result.data?.authenticateWithInvite;
        if (withInvite?.__typename !== "AuthenticateWithInviteSuccess") {
          return withInvite;
        }
        const {
          partner,
          ...success
        } = withInvite;
        return {
          ...success,
          __typename: "AuthenticateSuccess" as const,
          partner: {
            slug: partner.slug,
            name: partner.name
          }
        };
      }) : (await doAuthenticate({
        variables: {
          input
        }
      })).data?.authenticate;
      if (!authResult) {
        throw authError("No data returned from authenticate");
      }
      if (authResult.__typename === "AuthenticateSuccess") {
        // Log the user in on success. Otherwise the caller will handle the error on
        // the data object.
        if (!authResult.user) {
          throw authError("Missing user on response");
        }

        // Attach identity to the analytics clients BEFORE emitting any
        // auth-success events, so first_authentication / UserFirstAuthentication
        // carry user_id (RudderStack) and CUSTOM_USER_ID (Singular). The
        // profile-load effect re-identifies later to enrich traits (name,
        // segmentation); identify is idempotent. Shadow (admin-impersonation)
        // sessions are excluded to match that effect and avoid polluting data.
        if (!authResult.user.isShadow) {
          // Isolate telemetry from the auth path: this block is inside the
          // outer try whose catch turns any throw into authError, so an
          // analytics SDK hiccup must not fail an already-successful login.
          try {
            analyticsService.identifyUser(authResult.user.id, {
              email: authResult.email ?? undefined
            });
            singularService.setUser({
              id: authResult.user.id,
              email: authResult.email ?? undefined
            });
          } catch (error) {
            console.error(`Failed to identify user for analytics: ${String(error)}`);
          }
        }
        if (authResult.firstAuthentication) {
          singularService.event(SingularEvents.firstAuthentication);
          analyticsService.trackEvent("UserFirstAuthentication", {
            email: authResult.email ?? undefined
          });
        }
        if (mappedMethod === AuthenticateMethod.OAUTH) {
          // Clear the anonymous session token so it isn't re-sent on future logins.
          await anonymousSessionStorage.remove("token");
          if (userSegmentationData) {
            resetSignUpOnboardingStore();
            void userSegmentationStorage.remove("userSegmentationData");
          }
        }

        // This will also clear codeVerifier from state.
        const user = {
          ...authResult.user,
          // Can come back as `null` from GQL, so we convert to `undefined`.
          adminUserId: authResult.user?.adminUserId ?? undefined,
          firstAuthentication: authResult.firstAuthentication
        };
        dispatch({
          type: "LOGIN_SUCCEEDED",
          user
        });
        return authResult;
      }
      if (authResult.__typename === "AuthenticateError") {
        return authResult;
      }
      throw authError("Unexpected response type");
    } catch (error) {
      throw authError(error);
    }
  }, [doAuthenticate, doAuthenticateWithInvite, dispatch, getAuthenticationToken, state.codeVerifier, state.pendingInviteToken, resetSignUpOnboardingStore]);
  const setIntendedPath = useCallback((path: string) => {
    dispatch({
      type: "SET_INTENDED_PATH",
      intendedPath: path
    });
  }, [dispatch]);
  const getAndClearIntendedPath = useCallback((): string | null => {
    const intendedPath = state.intendedPath;
    if (intendedPath) {
      dispatch({
        type: "CLEAR_INTENDED_PATH"
      });
    }
    return intendedPath;
  }, [state.intendedPath, dispatch]);

  // Lets the current administrator user switch their session to a shadow session, where
  // they impersonate another user.
  const shadowLogin = useCallback(async (email: string) => {
    if (shadowLoginCalled) {
      // Another request is in-flight.
      return;
    }

    // Will throw any GQL/network errors up to the caller.
    const result = await doShadowLogin({
      variables: {
        input: {
          email
        }
      },
      context: errorHandling("error-screen")
    });

    // Reset the "called" flag so that we can try again.
    shadowLoginReset();
    const data = result.data?.shadowLogin;
    if (!data) {
      throw shadowLoginError("Missing data on response");
    }
    if (data.__typename === "AuthUser") {
      // Success!
      // The user data on the response only includes non-profile fields, so we need to
      // explicitly re-fetch profile data with the new session token.
      const profileData = await fetchProfile();
      const user = {
        ...data,
        // Can come back as `null` from GQL, so we convert to `undefined`.
        adminUserId: data.adminUserId ?? undefined,
        profile: profileData ? {
          name: profileData.profile.name ?? undefined,
          email: profileData.primaryEmail ?? ""
        } : undefined,
        userSegmentationData: profileData?.segmentationData?.features ? {
          features: profileData.segmentationData.features,
          useCases: undefined,
          usageScopes: undefined
        } : undefined
      };
      dispatch({
        type: "SHADOW_LOGIN",
        user
      });
      return data;
    }
    if (data.__typename === "FormError") {
      return data;
    }
    throw shadowLoginError("Unexpected response type");
  }, [dispatch, doShadowLogin, shadowLoginCalled, shadowLoginReset, fetchProfile]);
  const {
    startOAuth: startOAuthRedirect
  } = useStartOAuth(authRedirectUri);
  const startOAuth = useCallback(async (provider: OAuthProviderType, inviteToken?: string) => {
    dispatch({
      type: "PENDING_INVITE_TOKEN",
      pendingInviteToken: inviteToken ?? null
    });
    await startOAuthRedirect(provider);
  }, [dispatch, startOAuthRedirect]);

  // Memoize to prevent unnecessary re-renders
  const contextValue = useMemo<AuthContextInterface>(() => ({
    ...state,
    startOAuth,
    sendMagicLink,
    resendMagicLink,
    authenticate,
    logout,
    shadowLogin,
    finishOnboarding,
    setIntendedPath,
    getAndClearIntendedPath,
    refreshProfile
  }), [state, startOAuth, sendMagicLink, resendMagicLink, authenticate, logout, shadowLogin, finishOnboarding, setIntendedPath, getAndClearIntendedPath, refreshProfile]);
  return <AuthContext.Provider value={contextValue} data-sentry-element="AuthContext.Provider" data-sentry-component="AuthProvider" data-sentry-source-file="authProvider.tsx">
      {props.children}
    </AuthContext.Provider>;
};

// useAuth provides a React hook to lift the AuthContext and access state and methods.
export const useAuth = <TUser extends User = User,>(context = AuthContext): AuthContextInterface<TUser> => useContext(context) as AuthContextInterface<TUser>;